/**
 * RunMap generator & helpers.
 *
 * Layout (Ship 1 update): acts are two *disconnected* trees. Act 1 spans
 * rows 1..6 with 2–3 entry nodes at row 1 and no outbound edges from row 6.
 * Act 2 spans rows 7..12 (row 12 = single boss), with 2–3 fresh entry
 * nodes at row 7 that are NOT connected to any act-1 node. Completing any
 * row-6 act-1 node unlocks the row-7 entry set.
 *
 * Why disconnected: the player wanted act 2 to feel like a new tree, not
 * a continuation. Render-wise, the screen shows one act at a time.
 *
 *   1. Build act 1 internally with no-crossing edges.
 *   2. Build act 2 the same way, independently.
 *   3. Ensure each act has at least one HQ node.
 *   4. Assign cities from the enabled pool, no repeats within the map.
 */

import { CuratedCity } from '../types/run';
import {
  RunMap,
  RunMapNode,
  RunMapNodeType,
  ROWS_PER_ACT,
  TOTAL_ROWS,
  RUN_MAP_VERSION,
} from '../types/runMap';
import { generateAgentLoadout } from './agentLoadout';
import type { AgentLoadout } from '../types/game';

// --- RNG helpers ---

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function choice<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// --- Generation ---

function rollNodeType(isEntryRow: boolean): RunMapNodeType {
  if (isEntryRow) return 'hit';
  const r = Math.random();
  if (r < 0.22) return 'hq';
  if (r < 0.30) return 'elite';
  if (r < 0.38) return 'extraction';
  return 'hit';
}

/**
 * Build one act. Each act is a self-contained tree with its own entry row.
 * startRow is the absolute row index (1 for act 1, ROWS_PER_ACT + 1 for act 2).
 * If `bossAtEnd`, the final row has exactly one node of type 'boss'.
 */
function buildActNodes(startRow: number, bossAtEnd: boolean): RunMapNode[] {
  const nodes: RunMapNode[] = [];
  for (let i = 0; i < ROWS_PER_ACT; i++) {
    const row = startRow + i;
    const isEntryRow = i === 0;
    const isBossRow = bossAtEnd && i === ROWS_PER_ACT - 1;
    const width = isBossRow ? 1 : isEntryRow ? randInt(2, 3) : randInt(2, 4);
    for (let col = 0; col < width; col++) {
      const type: RunMapNodeType = isBossRow
        ? 'boss'
        : rollNodeType(isEntryRow);
      nodes.push({
        id: `r${row}c${col}`,
        row,
        col,
        type,
        cityName: null,
        nextNodeIds: [],
      });
    }
  }
  return nodes;
}

/** Build edges within an act — non-crossing, each source emits 1–3 outbound. */
function buildIntraActEdges(actNodes: RunMapNode[], startRow: number): void {
  const endRow = startRow + ROWS_PER_ACT - 1;
  for (let row = startRow; row < endRow; row++) {
    const current = actNodes.filter(n => n.row === row).sort((a, b) => a.col - b.col);
    const next = actNodes.filter(n => n.row === row + 1).sort((a, b) => a.col - b.col);
    if (current.length === 0 || next.length === 0) continue;

    let minTargetCol = 0;
    for (const src of current) {
      const outgoingCount = randInt(1, Math.min(3, next.length));
      const candidates = next.filter(t => t.col >= minTargetCol);
      if (candidates.length === 0) {
        const fallback = next[next.length - 1];
        src.nextNodeIds.push(fallback.id);
        minTargetCol = fallback.col;
        continue;
      }
      const preferred = [...candidates].sort(
        (a, b) => Math.abs(a.col - src.col) - Math.abs(b.col - src.col),
      );
      const picks = preferred.slice(0, outgoingCount);
      picks.sort((a, b) => a.col - b.col);
      for (const t of picks) src.nextNodeIds.push(t.id);
      minTargetCol = picks[picks.length - 1].col;
    }

    // Reverse validation — every target needs at least one inbound.
    const withInbound = new Set<string>();
    for (const src of current) for (const id of src.nextNodeIds) withInbound.add(id);
    for (const t of next) {
      if (!withInbound.has(t.id)) {
        const closest = [...current].sort(
          (a, b) => Math.abs(a.col - t.col) - Math.abs(b.col - t.col),
        )[0];
        closest.nextNodeIds.push(t.id);
      }
    }
  }
}

function ensureActHq(actNodes: RunMapNode[], startRow: number): void {
  // Skip the entry row and (for act 2) the boss row.
  const rowStart = startRow + 1;
  const rowEnd = startRow + ROWS_PER_ACT - 1; // may or may not be boss; filter below
  const middleNodes = actNodes.filter(n => n.row >= rowStart && n.row <= rowEnd && n.type !== 'boss');
  const hasHq = middleNodes.some(n => n.type === 'hq');
  if (!hasHq && middleNodes.length > 0) {
    const target = choice(middleNodes.filter(n => n.type === 'hit')) ?? middleNodes[0];
    target.type = 'hq';
  }
}

function assignCities(nodes: RunMapNode[], enabledCities: CuratedCity[]): void {
  const needsCity = nodes.filter(n => n.type !== 'hq');
  const pool = shuffle(enabledCities.map(c => c.name));
  for (let i = 0; i < needsCity.length; i++) {
    needsCity[i].cityName = pool[i % pool.length];
  }
}

/**
 * Pre-roll an agent loadout for every non-HQ node. Scaling is coarse on
 * purpose — difficulty progression is a post-Ship-2 task. For now:
 *   - Act 1 nodes → round-1 curve (knife/unarmed weapon pool, no defensive)
 *   - Act 2 nodes → round-4 curve (pistol-heavy, some sniper, more gear)
 *   - Boss       → round-5 curve (peak-tier kit)
 *
 * Simplified passive rule per user direction:
 *   - Hit          → 0 passives
 *   - Elite/Boss   → 2 passives
 *   - Extraction   → 0 passives (focus is on the extract, not tough fight)
 *
 * Codename dedup runs across all pre-rolls so the player never sees the
 * same codename twice on the map.
 */
function assignAgentLoadouts(nodes: RunMapNode[]): void {
  const usedCodenames: string[] = [];
  for (const node of nodes) {
    if (node.type === 'hq') continue;
    const baseRound = node.row > ROWS_PER_ACT ? 4 : 1;
    const round = node.type === 'boss' ? 5 : baseRound;
    const passiveCount =
      node.type === 'elite' ? 2 :
      node.type === 'boss' ? 2 :
      /* hit | extraction */ 0;
    const loadout: AgentLoadout = generateAgentLoadout(round, usedCodenames, { passiveCount });
    usedCodenames.push(loadout.codename);
    node.agentLoadout = loadout;
  }
}

export function generateRunMap(enabledCities: CuratedCity[]): RunMap {
  // Act 1 — rows 1..6, no boss, no outbound edges from row 6.
  const act1 = buildActNodes(1, false);
  buildIntraActEdges(act1, 1);
  ensureActHq(act1, 1);

  // Act 2 — rows 7..12, boss at row 12, disconnected from act 1.
  const act2Start = ROWS_PER_ACT + 1;
  const act2 = buildActNodes(act2Start, true);
  buildIntraActEdges(act2, act2Start);
  ensureActHq(act2, act2Start);

  const allNodes = [...act1, ...act2];
  assignCities(allNodes, enabledCities);
  assignAgentLoadouts(allNodes);

  const entryNodeIds = act1.filter(n => n.row === 1).map(n => n.id);
  const act2EntryNodeIds = act2.filter(n => n.row === act2Start).map(n => n.id);

  return {
    version: RUN_MAP_VERSION,
    nodes: allNodes,
    entryNodeIds,
    act2EntryNodeIds,
  };
}

// --- Helpers ---

export function getNode(map: RunMap, nodeId: string): RunMapNode | null {
  return map.nodes.find(n => n.id === nodeId) ?? null;
}

export function getBossNode(map: RunMap): RunMapNode | null {
  return map.nodes.find(n => n.type === 'boss') ?? null;
}

/**
 * Get the set of nodes the player can click next.
 *
 * - Pre-run (currentNodeId null) → act 1 entries.
 * - Current node is a row-6 act-1 node that's been completed → act 2 entries.
 * - Otherwise → current.nextNodeIds (intra-act progression).
 */
export function getReachableNodes(
  map: RunMap,
  currentNodeId: string | null,
  completedNodeIds: string[],
): RunMapNode[] {
  if (currentNodeId == null) {
    return map.entryNodeIds.map(id => getNode(map, id)).filter((n): n is RunMapNode => n != null);
  }
  const current = getNode(map, currentNodeId);
  if (!current) return [];
  // Transition from act 1 to act 2: row 6 completed → act 2 entries unlocked.
  if (current.row === ROWS_PER_ACT && completedNodeIds.includes(currentNodeId)) {
    const act2Entries = (map.act2EntryNodeIds ?? [])
      .map(id => getNode(map, id))
      .filter((n): n is RunMapNode => n != null);
    return act2Entries;
  }
  return current.nextNodeIds.map(id => getNode(map, id)).filter((n): n is RunMapNode => n != null);
}

/** Which act the UI should render right now. */
export function getCurrentAct(
  map: RunMap,
  currentNodeId: string | null,
  completedNodeIds: string[],
): 1 | 2 {
  if (!currentNodeId) return 1;
  const current = getNode(map, currentNodeId);
  if (!current) return 1;
  // A completed row-6 node means the player has finished act 1 — show act 2.
  if (current.row === ROWS_PER_ACT && completedNodeIds.includes(currentNodeId)) {
    return 2;
  }
  return current.row > ROWS_PER_ACT ? 2 : 1;
}

/** Descendants of a node within the same act (reachable via nextNodeIds). */
export function getDescendantIds(map: RunMap, nodeId: string): Set<string> {
  const out = new Set<string>();
  const queue = [nodeId];
  while (queue.length) {
    const id = queue.shift()!;
    const node = getNode(map, id);
    if (!node) continue;
    for (const nxt of node.nextNodeIds) {
      if (!out.has(nxt)) {
        out.add(nxt);
        queue.push(nxt);
      }
    }
  }
  return out;
}

/** Rows belonging to an act (for filtered rendering). */
export function actRows(act: 1 | 2): number[] {
  const start = act === 1 ? 1 : ROWS_PER_ACT + 1;
  const rows: number[] = [];
  for (let r = start; r < start + ROWS_PER_ACT; r++) rows.push(r);
  return rows;
}

// Suppress unused import warnings for constants the type file exposes.
export { TOTAL_ROWS };
